import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { FunctionsHttpError } from "@supabase/supabase-js";

const clientToken = import.meta.env.VITE_PAYMENTS_CLIENT_TOKEN as string | undefined;

declare global {
  interface Window {
    Paddle: any;
  }
}

export function getPaddleEnvironment(): "sandbox" | "live" {
  return clientToken?.startsWith("test_") ? "sandbox" : "live";
}

export const isPaddleConfigured = () => !!clientToken;

let paddleInitialized = false;

export async function initPaddle(): Promise<void> {
  if (paddleInitialized) return;
  if (!clientToken) throw new Error("Payments are not configured yet.");

  await new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-paddle="v2"]');
    const script = existing ?? document.createElement("script");
    const onLoad = () => {
      window.Paddle.Environment.set(
        getPaddleEnvironment() === "sandbox" ? "sandbox" : "production",
      );
      window.Paddle.Initialize({
        token: clientToken,
        eventCallback: (event: any) => {
          if (event?.name === "checkout.error") {
            console.error("Paddle checkout error:", event.data);
            toast.error("Payment page could not open", {
              description: "Please try again in a moment.",
            });
          }
        },
      });
      paddleInitialized = true;
      resolve();
    };
    if (existing && window.Paddle) return onLoad();
    script.src = "https://cdn.paddle.com/paddle/v2/paddle.js";
    script.dataset.paddle = "v2";
    script.onload = onLoad;
    script.onerror = reject;
    if (!existing) document.head.appendChild(script);
  });
}

export async function getPaddlePriceId(priceId: string): Promise<string> {
  const environment = getPaddleEnvironment();
  const { data, error } = await supabase.functions.invoke("get-paddle-price", {
    body: { priceId, environment },
  });
  if (error || !(data as any)?.paddleId) {
    let detail = (data as { error?: string } | null)?.error;
    if (error instanceof FunctionsHttpError) {
      try {
        const response = await error.context.json();
        detail = response?.error || detail;
      } catch {
        detail = error.message;
      }
    }
    throw new Error(`Could not load ${priceId.replace("_monthly", "")} checkout: ${detail || "please try again shortly."}`);
  }
  return (data as any).paddleId;
}

interface CheckoutArgs {
  priceId: string;
  email?: string;
  userId: string;
  planId?: string;
  successUrl?: string;
}

export const openPaddleCheckout = async ({
  priceId,
  email,
  userId,
  planId,
  successUrl,
}: CheckoutArgs) => {
  await initPaddle();
  const paddlePriceId = await getPaddlePriceId(priceId);

  window.Paddle.Checkout.open({
    items: [{ priceId: paddlePriceId, quantity: 1 }],
    customer: email ? { email } : undefined,
    customData: { userId, plan: planId ?? "" },
    settings: {
      displayMode: "overlay",
      theme: "dark",
      allowLogout: false,
      variant: "one-page",
      successUrl: successUrl ?? `${window.location.origin}/billing?checkout=success`,
    },
  });
};
